import MatcherPlugin from '@bbc/news-vj-search-lookup-plugin-matcher';
import pegasus from '@typicode/pegasus';

/**
 * Determines if user input is a postcode, and attempts a query to LocServices.
 * Also caches good responses to consistently display suggestions when queries are not possible
 */
export default class CustomPostcodePlugin {
    /**
     * Creates an instance of CustomPostcodePlugin.
     * @param {Object} config
     * @param {Function} config.intersectCallback - Custom function to modify intersect parameter
     * @param {(String|Function)} [config.searchType = 'gss-council'] - Either a custom callback or a string to modify the `detail_types` parameter
     * @param {String} - config.apiKey - Key for using the location services API
     */
    constructor(
        {
            intersectCallback = postcode => CustomPostcodePlugin.isFullPostcode(postcode),
            searchType = 'gss-council',
            apiKey = 'WdmvTVscFy19vwu3UG82gTQ3HtaaLsVp',
        },
    ) {
        this.queryConfig = {
            locservicesBaseURL: 'https://locator-service.api.bbci.co.uk/locations',
            endpoint: 'details',
            intersectCallback: intersectCallback,
            searchType: searchType,
            params: {
                postcode: '',
                op: '',
                format: 'json',
                vv: '2',
                rows: '',
                detail_types: searchType,
                api_key: apiKey,
            },
        };

        this.encodeParams = this.encodeParams.bind(this);
    }

    /**
     * Query hook for lookup module
     * @param {String} query - The current user input
     * @param {Function} done - Called to provide a result and stop any other plugins being queried
     * @param {Function} skip - Called to advance to next plugin, optionally with `{ suppressError: true }` to prevent a 'no results' error
     * @param {Function} error - Called to provide a custom error message
     * @param {Object} localResults - Reference to any local suggestions
     * @param {Array} dataset - Reference to the lookup module dataset
     */
    query(query, done, skip, error, localResults, dataset) {
        const postcode = CustomPostcodePlugin.sanitisePostcode(query);
        const hasCache = !!this.cache;
        const hasCachedResponse = hasCache && this.cache.response.length > 0;
        const isSameQuery = hasCache
            && CustomPostcodePlugin.isSimilarQuery(this.cache.postcode, postcode);

        const { searchType } = this.queryConfig;
        this.queryConfig.params['detail_types'] = typeof searchType === 'function'
            ? searchType(postcode)
            : searchType;

        if (CustomPostcodePlugin.isPostcode(postcode)) {
            const shouldIntersect = this.queryConfig.intersectCallback(postcode);
            this.queryConfig.params.rows = shouldIntersect ? '' : '6';
            this.queryConfig.params.postcode = postcode;

            // We don't need this now, but may need it in the future
            // if (CustomPostcodePlugin.isFullPostcode(postcode) && !CustomPostcodePlugin.isEnglishPostcode(postcode)) {
            //     this.queryConfig.params.rows = '1';
            // }

            const requestURL = this.constructURL();
            return pegasus(requestURL).then(response => {
                const responses = this.formatResponse(response.response.content.details.details, dataset, response.response.metadata, postcode);

                this.cacheResults({
                    postcode: postcode,
                    response: responses,
                });
                return done(responses);
            });
        }

        if (hasCachedResponse && isSameQuery) {
            return done(this.cache.response);
        }

        // if (CustomPostcodePlugin.couldBePostcode(postcode)) {
        //     return skip({ suppressError: true });
        // }

        return skip();
    }

    /**
     * Checks if cached query is a substring of current query and the inverse
     * @param {String} cache - Cached reference to a valid postcode search
     * @param {String} currentQuery - The current user input
     * @returns {Bool}
     */
    static isSimilarQuery(cache, currentQuery) {
        return currentQuery.indexOf(cache) >= 0 || cache.indexOf(currentQuery) >= 0;
    }

    /**
     * Filters out any response that isn't in the local dataset (by GSSID)
     * @param {Object} response - Response object from LocServices API
     * @param {Array} dataset - Reference to local dataset
     * @returns {Array} Usable entries from the response
     */
    filterInvalidResponses(response, dataset) {
        const validIds = dataset.map(d => d.id);
        return response.content.details.details.filter(d => validIds.indexOf(d.externalId) > -1);
    }

    /**
     * Maps results to a lookup-friendly format
     * @param {Array} results - List of objects from the LocServices API
     * @param {Array} dataset - Reference to local dataset
     * @returns {Array} List of objects in expected format
     */

    formatResponse(results, dataset, metadata, postcode) {
        // remove english CCGs (mostly appearing in welsh responses)
        let formattedResults = results.filter(result => result.data.entityAbbr !== 'CCG');
        formattedResults = formattedResults.filter(result => result.externalId !== 'RPC')
            .sort((a, b) => (a.distance - b.distance));

        if (CustomPostcodePlugin.isFullPostcode(postcode)) {
            const nation = metadata.location.admin1.name;

            // If it's a full postcode, we only want to keep nhs-trust for England
            if (nation === 'England') {
                formattedResults = formattedResults.filter(result => result.detailType === 'nhs-trust');
            }
            // And we only want to keep gss-health for everything else
            // Plus, we return only the top result for non-English postcodes
            else {
                formattedResults = formattedResults
                    .filter(
                        (result) => result.detailType === 'gss-health',
                    )
                    .slice(0, 1);
            }
        }
        return formattedResults.map(d => ({ // eslint-disable-line
            id: d.externalId,
            data: dataset.filter(datum => datum.id === d.externalId)[0].data,
        }));
    }

    /**
     * Saves a response to the cache
     * @param {Object} config
     * @param {String} config.postcode - A valid postcode
     * @param {Array} config.response - A list of formatted and validated responses
     */
    cacheResults({ postcode, response }) {
        this.cache = {
            postcode: postcode,
            response: response,
        };
    }

    /**
     * Uses the config to construct a URL to call LocServices API
     * @returns {String} - A URL with query paramaters
     * [current]: Amends calls for NI to use intersect; requires other parameters to disappear
     */
    constructURL() {
        const parts = [
            this.queryConfig.locservicesBaseURL,
            this.queryConfig.params.postcode,
            this.queryConfig.endpoint,
            this.queryConfig.params['detail_types'],
        ];
        const baseURL = parts.join('/');
        const params = Object.keys(this.queryConfig.params);
        const encodedParams = params.reduce(this.encodeParams, '');
        return `${baseURL}?${encodedParams}`;
    }

    /**
     * Reduce function to turn a list of strings into a URL query
     * @param {String} string - (Accumulator) The entire query
     * @param {Param} param - (Current) The current parameter
     * @returns {String}
     */
    encodeParams(string, param) {
        return this.queryConfig.params[param] !== ''
            ? `${string}&${param}=${this.queryConfig.params[param]}`
            : string;
    }

    /**
     * Checks if string is a postcode
     * @param {String} string
     * @returns {Bool}
     */
    static isPostcode(string) {
        return string.length <= 8 &&
            string.length >= 2 &&
            (CustomPostcodePlugin.isFullPostcode(string) || CustomPostcodePlugin.isPartialPostcode(string));
    }

    /**
     * Similar to `isPostcode`, checks if string _could_ be a postcode
     * @param {String} string
     * @returns {Bool}
     */
    static couldBePostcode(string) {
        return string.length > 1 && string.length <= 8 && !string.toUpperCase().charAt(2).match(/([A-Z])/i);
    }

    /**
     * Validates string against an almighty RegEx that determines if a string is a full UK postcode
     * Not 100% accurate
     * @param {String} string
     * @returns {Bool}
     */
    static isFullPostcode(string) {
        const regex = /^(GIR 0AA|[A-PR-UWYZ]([0-9]{1,2}|([A-HK-Y][0-9]|[A-HK-Y][0-9]([0-9]|[ABEHMNPRV-Y]))|[0-9][A-HJKPS-UW]) *[0-9][ABD-HJLNP-UW-Z]{2})$/;
        return regex.test(CustomPostcodePlugin.sanitisePostcode(string));
    }

    /**
     * Validates string against an almighty RegEx that determines if a string is a partial UK postcode
     * @param {String} string
     * @returns {Bool}
     */
    static isPartialPostcode(string) {
        const regex = /^(GIR|[A-PR-UWYZ]([A-Z]|[0-9]{1,2}|([A-HK-Y][0-9]|[A-HK-Y][0-9]([0-9]|[ABEHMNPRV-Y]))|[0-9][A-HJKPS-UW]))/;
        return regex.test(CustomPostcodePlugin.sanitisePostcode(string));
    }

    /**
     * Mutates string to upper case and removes any spaces
     * @param {String} string
     * @returns {String} Uppercase without spaces
     */
    static sanitisePostcode(string) {
        return string.toUpperCase().replace(/ /g, '');
    }

    static isRegionPostcode(region, postcode) {
        const { expressions } = MatcherPlugin;
        return expressions[region].partial.test(postcode);
    }

    static isEnglishPostcode(postcode) {
        const isWelsh = CustomPostcodePlugin.isRegionPostcode('wales', postcode);
        const isScottish = CustomPostcodePlugin.isRegionPostcode('scotland', postcode);
        const isNorthernIrish = CustomPostcodePlugin.isRegionPostcode('northernIreland', postcode);
        return (!isWelsh && !isScottish && !isNorthernIrish);
    }

    static isBorderPostcode(postcode) {
        // border postcodes return true for both england and wales, or england and scotland
        return CustomPostcodePlugin.isEnglishPostcode(postcode) &&
            (CustomPostcodePlugin.isRegionPostcode('wales', postcode) || CustomPostcodePlugin.isRegionPostcode('scotland', postcode));
    }
}
